import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import RecruiterSidebar from '../components/RecruiterSidebar';
import { API } from '../config';
import '../styles/theme.css';

const STATUS_COLORS = {
  applied: '#5d1a70', pending: '#5d1a70', viewed: '#3b82f6',
  interview: '#f59e0b', hired: '#10b981', rejected: '#ef4444',
};

function FunnelBar({ label, count, pct, color }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: '0.875rem', color: 'var(--text-primary)', fontWeight: 500 }}>{label}</span>
        <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>{count} ({pct}%)</span>
      </div>
      <div style={{ height: 10, background: 'var(--border)', borderRadius: 6, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 6, transition: 'width 0.6s ease' }} />
      </div>
    </div>
  );
}

export default function RecruiterAnalytics() {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetch(`${API}/insights/recruiter-analytics`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json().then(j => ({ ok: r.ok, json: j })))
      .then(({ ok, json }) => {
        if (!ok) throw new Error(json.detail || 'Failed');
        setData(json);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const isPaid = error?.toLowerCase().includes('professional') || error?.toLowerCase().includes('upgrade');

  return (
    <div className="portal-layout">
      <RecruiterSidebar />
      <main className="portal-main">
        <div className="portal-topbar">
          <span className="topbar-title">Analytics</span>
          <div className="topbar-actions">
            <button className="topbar-btn" onClick={toggleTheme}>
              <i className={`fas ${isDark ? 'fa-sun' : 'fa-moon'}`}></i>
            </button>
          </div>
        </div>

        <div className="portal-content">
          <div className="page-header">
            <h1>
              Recruitment Analytics{' '}
              <span className="badge badge-blue" style={{ fontSize: '0.7rem', verticalAlign: 'middle' }}>Professional</span>
            </h1>
            <p>Track your hiring pipeline and job performance</p>
          </div>

          {loading && (
            <div className="empty-state">
              <i className="fas fa-spinner fa-spin" style={{ opacity: 1, color: 'var(--primary)' }}></i>
              <p>Loading analytics...</p>
            </div>
          )}

          {!loading && error && isPaid && (
            <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
              <i className="fas fa-lock" style={{ fontSize: '3rem', color: 'var(--text-muted)', marginBottom: '1rem', display: 'block' }} />
              <h3 style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>Professional Feature</h3>
              <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
                Analytics are available on the Professional plan (R499/month).
              </p>
              <button className="btn btn-primary" onClick={() => navigate('/pricing')}>
                <i className="fas fa-crown"></i> Upgrade to Professional
              </button>
            </div>
          )}

          {!loading && error && !isPaid && (
            <div className="alert alert-error"><i className="fas fa-exclamation-circle"></i> {error}</div>
          )}

          {!loading && data && (
            <>
              {/* Top stats */}
              <div className="stats-row" style={{ marginBottom: '1.5rem' }}>
                <div className="stat-card">
                  <div className="stat-icon-wrap blue"><i className="fas fa-briefcase"></i></div>
                  <div className="stat-info">
                    <div className="stat-value">{data.jobs_total}</div>
                    <div className="stat-label">Total Jobs</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon-wrap green"><i className="fas fa-check-circle"></i></div>
                  <div className="stat-info">
                    <div className="stat-value">{data.jobs_active}</div>
                    <div className="stat-label">Active Jobs</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon-wrap purple"><i className="fas fa-file-alt"></i></div>
                  <div className="stat-info">
                    <div className="stat-value">{data.applications_total}</div>
                    <div className="stat-label">Total Applications</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon-wrap orange"><i className="fas fa-bullseye"></i></div>
                  <div className="stat-info">
                    <div className="stat-value">{data.avg_match_score != null ? `${data.avg_match_score}%` : '—'}</div>
                    <div className="stat-label">Avg Match Score</div>
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                {/* Hiring funnel */}
                <div className="card">
                  <div className="card-header"><span className="card-title">Hiring Funnel</span></div>
                  <div style={{ padding: '0.5rem 0' }}>
                    {data.applications_total === 0 ? (
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>No applications yet.</p>
                    ) : (
                      <>
                        <FunnelBar label="Applied / Pending" count={data.funnel.applied.count} pct={data.funnel.applied.pct} color="#5d1a70" />
                        <FunnelBar label="Viewed" count={data.funnel.viewed.count} pct={data.funnel.viewed.pct} color="#3b82f6" />
                        <FunnelBar label="Interview" count={data.funnel.interview.count} pct={data.funnel.interview.pct} color="#f59e0b" />
                        <FunnelBar label="Hired" count={data.funnel.hired.count} pct={data.funnel.hired.pct} color="#10b981" />
                      </>
                    )}
                  </div>
                </div>

                {/* Status breakdown */}
                <div className="card">
                  <div className="card-header"><span className="card-title">Applications by Status</span></div>
                  {Object.keys(data.by_status).length === 0 ? (
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>No data yet.</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', paddingTop: '0.5rem' }}>
                      {Object.entries(data.by_status).map(([status, count]) => (
                        <div key={status} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            fontSize: '0.875rem', color: 'var(--text-primary)', textTransform: 'capitalize'
                          }}>
                            <span style={{
                              width: 10, height: 10, borderRadius: '50%',
                              background: STATUS_COLORS[status.toLowerCase()] || '#94a3b8',
                              display: 'inline-block', flexShrink: 0
                            }} />
                            {status}
                          </span>
                          <span className="badge badge-blue">{count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Top jobs by applications */}
              <div className="card">
                <div className="card-header"><span className="card-title">Top Jobs by Applications</span></div>
                {data.by_job.length === 0 ? (
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>No applications yet.</p>
                ) : (
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Job Title</th>
                          <th>Status</th>
                          <th>Applications</th>
                          <th>Bar</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.by_job.map(row => {
                          const maxApps = data.by_job[0]?.applications || 1;
                          return (
                            <tr key={row.job_id}>
                              <td style={{ fontWeight: 500 }}>{row.title}</td>
                              <td>
                                <span className={`badge ${row.is_active ? 'badge-green' : 'badge-red'}`}>
                                  {row.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td>{row.applications}</td>
                              <td style={{ width: '35%' }}>
                                <div style={{ height: 8, background: 'var(--border)', borderRadius: 4, overflow: 'hidden' }}>
                                  <div style={{
                                    height: '100%', borderRadius: 4,
                                    background: 'linear-gradient(90deg, #5d1a70, #640ff7)',
                                    width: `${(row.applications / maxApps) * 100}%`,
                                    transition: 'width 0.6s ease'
                                  }} />
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Applications over time */}
              {data.over_time?.length > 0 && (
                <div className="card" style={{ marginTop: '1.5rem' }}>
                  <div className="card-header"><span className="card-title">Applications — Last 30 Days</span></div>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem', height: 120, padding: '0.5rem 0' }}>
                    {data.over_time.map(({ week, count }) => {
                      const maxCount = Math.max(...data.over_time.map(w => w.count), 1);
                      return (
                        <div key={week} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                          <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{count}</span>
                          <div style={{
                            width: '100%', borderRadius: '4px 4px 0 0',
                            background: 'linear-gradient(180deg, #640ff7, #5d1a70)',
                            height: `${(count / maxCount) * 80}px`,
                            minHeight: 4,
                          }} />
                          <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{week}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
